import { useEffect, useMemo } from "react";

import { useFormContext } from "@ui/components/Form";
import type { Translate } from "@ui/providers/useTranslations";
import { useTranslations } from "@ui/providers/useTranslations";

import type { PasswordRequirement } from "@jti/core-components/components/Password";
import type { PasswordRequirementsResponse } from "@jti/fe-common";
import { usePasswordRules } from "@jti/rest-api";

import type { PasswordRequirementKey } from "../utils";
import { hasUnmetRequirements, isRequirementEnabled, PASSWORD_REQUIREMENT_KEYS, validatePassword } from "../utils";

const VALIDATION_KEY = "password";

const getRequirementLabel = (key: PasswordRequirementKey, rules: PasswordRequirementsResponse, __: Translate) => {
  switch (key) {
    case "passwordMinLength":
      return __("auth.registration.password_info_message_length", [rules.minLength]);
    case "passwordMaxLength":
      return __("auth.registration.password_info_message_max_length");
    case "containNumber":
      return __("auth.registration.password_info_message_number", [rules.minNumber]);
    case "containSpecial":
      return __("auth.registration.password_info_message_character", [rules.minSymbol]);
    case "containLettersLowerCase":
      return __("auth.registration.password_info_message_letter_lower_case", [rules.minLowerCase]);
    case "containLettersUpperCase":
      return __("auth.registration.password_info_message_letter_upper_case", [rules.minUpperCase]);
    case "excludeUsername":
      return __("auth.registration.password_exclude_username_rule");
    default:
      return "";
  }
};

type UsePasswordRequirementsParams = {
  passwordValue: string;
  email?: string;
  enabled?: boolean;
};

type UsePasswordRequirementsResult = {
  requirements: PasswordRequirement[];
  isPending: boolean;
};

export const usePasswordRequirements = ({
  passwordValue,
  email,
  enabled = true,
}: UsePasswordRequirementsParams): UsePasswordRequirementsResult => {
  const { __ } = useTranslations();
  const { setError, clearErrors } = useFormContext();
  const { data: passwordRules, isLoading } = usePasswordRules(!enabled);

  const validationResult = useMemo(
    () => (passwordRules && !isLoading ? validatePassword(passwordValue, passwordRules, email) : undefined),
    [email, isLoading, passwordRules, passwordValue]
  );

  useEffect(() => {
    if (!enabled || !validationResult) return;

    if (hasUnmetRequirements(validationResult)) {
      setError(VALIDATION_KEY, { type: "manual" });
    } else {
      clearErrors(VALIDATION_KEY);
    }
  }, [clearErrors, enabled, setError, validationResult]);

  const requirements = useMemo<PasswordRequirement[]>(() => {
    if (!enabled || !passwordRules || !validationResult) return [];

    return PASSWORD_REQUIREMENT_KEYS.filter((key) => isRequirementEnabled(key, passwordRules)).map((key) => ({
      label: getRequirementLabel(key, passwordRules, __),
      isMet: validationResult[key],
    }));
  }, [__, enabled, passwordRules, validationResult]);

  return {
    requirements,
    isPending: enabled && isLoading && !passwordRules,
  };
};
